import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, 
  Download, 
  Trash2, 
  Eye,
  Database,
  Brain,
  MessageSquare,
  FileText,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ExternalLink,
  Lock,
  Server
} from 'lucide-react';

/**
 * Privacy Center - Personal Twin Data Sovereignty
 * 
 * Purpose: Give users full control and visibility over their Twin data
 * 
 * Features:
 * - View stored data categories
 * - Inspect what Twin knows (link to Identity Formation)
 * - Export personal data (JSON)
 * - Request account/data deletion
 * - AI processing transparency
 */

export default function PrivacyCenter() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dataStats, setDataStats] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetchDataStats();
  }, []);

  const fetchDataStats = async () => {
    try {
      const [identitySummary, representationHistory] = await Promise.all([
        apiClient.get('/api/identity-evidence/summary').catch(() => ({ data: {} })),
        apiClient.get('/api/representation/history?limit=1').catch(() => ({ data: { total: 0 } })),
      ]);

      setDataStats({
        identityEvidence: identitySummary.data.total_evidence_count || 0,
        confirmedTraits: identitySummary.data.confirmed_traits_count || 0,
        emergingPatterns: identitySummary.data.emerging_patterns_count || 0,
        representations: representationHistory.data.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch data stats:', error);
      toast.error(t('errors.loadFailed', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await apiClient.get('/api/privacy/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `humatwin-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('privacy.exportSuccess', 'Data exported successfully'));
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('privacy.exportFailed', 'Failed to export data'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error(t('privacy.typeDeleteToConfirm', 'Please type DELETE to confirm'));
      return;
    }

    setDeleteLoading(true);
    try {
      await apiClient.post('/api/privacy/delete-request');
      toast.success(t('privacy.deleteRequestSuccess', 'Deletion request submitted'));
      setDeleteDialogOpen(false);
      // Redirect to login after brief delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Delete request failed:', error);
      toast.error(t('privacy.deleteRequestFailed', 'Failed to submit deletion request'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const dataCategories = [
    {
      icon: Database,
      title: t('privacy.category.account', 'Account Information'),
      description: t('privacy.category.accountDesc', 'Your email, name, and account settings'),
      count: null,
    },
    {
      icon: MessageSquare,
      title: t('privacy.category.identitySessions', 'Identity Sessions'),
      description: t('privacy.category.identitySessionsDesc', 'Conversations exploring how you think'),
      count: null,
    },
    {
      icon: FileText,
      title: t('privacy.category.reflections', 'Daily Reflections'),
      description: t('privacy.category.reflectionsDesc', 'Your personal reflections and journal entries'),
      count: null,
    },
    {
      icon: Brain,
      title: t('privacy.category.identityEvidence', 'Identity Observations'),
      description: t('privacy.category.identityEvidenceDesc', 'Patterns your Twin has observed about you'),
      count: dataStats?.identityEvidence,
      link: '/personal/identity-formation',
    },
    {
      icon: CheckCircle2,
      title: t('privacy.category.confirmedIdentity', 'Confirmed Identity'),
      description: t('privacy.category.confirmedIdentityDesc', 'Traits you have confirmed as representing you'),
      count: dataStats?.confirmedTraits,
      link: '/personal/identity-formation',
    },
    {
      icon: RefreshCw,
      title: t('privacy.category.representations', 'Twin Representations'),
      description: t('privacy.category.representationsDesc', 'Scenarios where your Twin represented your perspective'),
      count: dataStats?.representations,
      link: '/personal/representation',
    },
  ];

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="privacy-center-title">
              {t('privacy.title', 'Privacy & Data Control')}
            </h1>
            <p className="text-slate-600">
              {t('privacy.subtitle', 'Understand and control your Personal Twin data')}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* What Your Twin Knows */}
          <Card data-testid="what-twin-knows">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                {t('privacy.whatTwinKnows', 'What Your Twin Knows')}
              </CardTitle>
              <CardDescription>
                {t('privacy.whatTwinKnowsDesc', 'View and inspect the information your Twin has learned about you')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {dataCategories.map((category, i) => {
                  const Icon = category.icon;
                  return (
                    <div 
                      key={i}
                      className={`p-4 rounded-lg border border-slate-200 ${category.link ? 'hover:border-slate-300 cursor-pointer' : ''}`}
                      onClick={() => category.link && navigate(category.link)}
                      data-testid={`data-category-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-slate-900 truncate">{category.title}</h3>
                            {category.count !== null && (
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                {category.count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{category.description}</p>
                          {category.link && (
                            <span className="text-xs text-slate-400 flex items-center gap-1 mt-2">
                              {t('privacy.viewDetails', 'View details')}
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* AI Processing Transparency */}
          <Card data-testid="ai-transparency">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                {t('privacy.aiProcessing', 'AI Processing')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-700 text-sm leading-relaxed">
                  {t('privacy.aiProcessingDesc', 'To build your Twin, some of your content is processed by AI providers (such as OpenAI) for:')}
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {t('privacy.aiUse1', 'Extracting identity signals from conversations')}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {t('privacy.aiUse2', 'Generating representations of your perspective')}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {t('privacy.aiUse3', 'Comparing semantic similarity for evaluation')}
                  </li>
                </ul>
                <p className="mt-4 text-sm text-slate-500">
                  {t('privacy.aiNote', 'Only necessary context is sent. Your data is not sold or shared with third parties for their own purposes.')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Actions */}
          <Card data-testid="data-actions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {t('privacy.dataActions', 'Your Data Rights')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Export */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-slate-600" />
                  <div>
                    <h3 className="font-medium text-slate-900">
                      {t('privacy.exportData', 'Export Your Data')}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {t('privacy.exportDataDesc', 'Download all your Twin data in JSON format')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleExport}
                  disabled={exportLoading}
                  data-testid="export-data-btn"
                >
                  {exportLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('privacy.export', 'Export')
                  )}
                </Button>
              </div>

              {/* Delete */}
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <div>
                    <h3 className="font-medium text-red-900">
                      {t('privacy.deleteAccount', 'Delete Account & Data')}
                    </h3>
                    <p className="text-sm text-red-700">
                      {t('privacy.deleteAccountDesc', 'Permanently delete your Twin and all associated data')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-account-btn"
                >
                  {t('privacy.delete', 'Delete')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Legal Placeholders */}
          <Card data-testid="legal-links">
            <CardHeader>
              <CardTitle>{t('privacy.legalTitle', 'Legal & Policies')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <a 
                  href="/privacy-policy" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="text-slate-700">{t('privacy.privacyPolicy', 'Privacy Policy')}</span>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {t('privacy.pendingReview', 'Pending Legal Review')}
                  </span>
                </a>
                <a 
                  href="/terms" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="text-slate-700">{t('privacy.termsOfUse', 'Terms of Use')}</span>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {t('privacy.pendingReview', 'Pending Legal Review')}
                  </span>
                </a>
                <a 
                  href="/contact" 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="text-slate-700">{t('privacy.contactDataRights', 'Contact / Data Rights Requests')}</span>
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t('privacy.deleteConfirmTitle', 'Delete Your Account')}
            </DialogTitle>
            <DialogDescription>
              {t('privacy.deleteConfirmDesc', 'This action cannot be undone. All your data will be permanently deleted, including:')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4">
            <ul className="space-y-2 text-sm text-slate-600">
              <li>• {t('privacy.deleteItem1', 'Your account and profile')}</li>
              <li>• {t('privacy.deleteItem2', 'All identity evidence and confirmed traits')}</li>
              <li>• {t('privacy.deleteItem3', 'All reflections and knowledge')}</li>
              <li>• {t('privacy.deleteItem4', 'All representation history and evaluations')}</li>
              <li>• {t('privacy.deleteItem5', 'All corrections and feedback')}</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="delete-confirm">
              {t('privacy.typeDelete', 'Type DELETE to confirm')}
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              data-testid="delete-confirm-input"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteLoading}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('privacy.confirmDelete', 'Delete My Account')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
